import { Outlet, createRootRouteWithContext, createRoute, createRouter, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ApiError, api, qk, setCsrfToken } from '@nw/web-platform';
import type { Me } from '@nw/contracts';
import { Shell } from './shell/Shell.js';
import { Toaster } from './shell/Toaster.js';
import { HomePage } from './pages/HomePage.js';
import { SignInPage } from './pages/SignInPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { windowRoutes } from './routes.js';

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  validateSearch: z.object({ returnTo: z.string().optional() }),
  component: SignInPage,
});

/** Everything below requires a verified session; the workspace comes from the server. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.fetchQuery({
        queryKey: qk.me,
        queryFn: async () => (await api<Me>('/me')).data,
        staleTime: 60_000,
      });
      setCsrfToken(me.csrfToken);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/signin', search: { returnTo: location.href } });
      }
      throw err;
    }
  },
  component: Shell,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomePage });

const routeTree = rootRoute.addChildren([signInRoute, appRoute.addChildren([homeRoute, ...windowRoutes(appRoute)])]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultNotFoundComponent: NotFoundPage,
  });
}
